import type {
  CoachDevelopmentMemory,
  CoachLeaderSessionContinuity,
  LeaderDevelopmentMemory,
  SessionParticipantProfile,
} from "@/lib/behavior/behaviorContracts";
import {
  extractCoachReviewV1,
  extractLeaderPlanDraftV1,
} from "@/lib/coachSessionReviewContract";
import {
  buildCoachDevelopmentMemory,
  buildLeaderDevelopmentMemory,
} from "@/lib/behavior/coachSessionInterpretation";

export type RawCoachSessionRecord = {
  id: string;
  leader_id: string | null;
  transcript_upload_id: string | null;
  created_at: string | null;
  worked_on: unknown;
  direction: unknown;
  next_conversation_signals: unknown;
  ambiguity_flag?: boolean | null;
  ambiguity_reasons?: unknown;
  coach_review_v1?: unknown;
  leader_plan_draft_v1?: unknown;
  published_to_leader?: boolean | null;
};

export type NormalizedCoachLeaderSessionRecord = {
  recordId: string;
  leaderId: string | null;
  transcriptUploadId: string | null;
  createdAt: string | null;
  publishedToLeader: boolean;
  ambiguityFlag: boolean;
  ambiguityReasons: string[];
  leader: SessionParticipantProfile | null;
  leaderDevelopment: LeaderDevelopmentMemory;
  coachDevelopment: CoachDevelopmentMemory | null;
  leaderContinuitySource: "typed" | "legacy";
  approvalState: "pending" | "approved" | "rejected";
};

function normalizeLine(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string")
    .map(normalizeLine)
    .filter(Boolean);
}

export function normalizeCoachLeaderSessionRecord(params: {
  record: RawCoachSessionRecord;
  leader?: SessionParticipantProfile | null;
}): NormalizedCoachLeaderSessionRecord {
  const coachReview = extractCoachReviewV1(params.record.coach_review_v1);
  const leaderPlanDraft = extractLeaderPlanDraftV1(params.record.leader_plan_draft_v1);
  const publishedToLeader = params.record.published_to_leader ?? false;

  return {
    recordId: params.record.id,
    leaderId: params.record.leader_id ?? null,
    transcriptUploadId: params.record.transcript_upload_id ?? null,
    createdAt: params.record.created_at ?? null,
    publishedToLeader,
    ambiguityFlag: params.record.ambiguity_flag ?? false,
    ambiguityReasons: toStringArray(params.record.ambiguity_reasons),
    leader: params.leader ?? null,
    leaderContinuitySource: leaderPlanDraft ? "typed" : "legacy",
    approvalState: leaderPlanDraft?.approvalState ?? (publishedToLeader ? "approved" : "pending"),
    leaderDevelopment: leaderPlanDraft
      ? buildLeaderDevelopmentMemory({ leaderPlanDraft })
      : {
          discussed: toStringArray(params.record.worked_on),
          nextFocus: toStringArray(params.record.direction),
          nextSession: toStringArray(params.record.next_conversation_signals),
          actionItems: [],
          reflectionPrompts: [],
        },
    coachDevelopment: coachReview ? buildCoachDevelopmentMemory({ coachReview }) : null,
  };
}

export function buildCoachLeaderSessionContinuity(params: {
  record: RawCoachSessionRecord;
  leader?: SessionParticipantProfile | null;
}): CoachLeaderSessionContinuity {
  const normalized = normalizeCoachLeaderSessionRecord(params);

  return {
    recordId: normalized.recordId,
    leaderId: normalized.leaderId,
    transcriptUploadId: normalized.transcriptUploadId,
    createdAt: normalized.createdAt,
    publishedToLeader: normalized.publishedToLeader,
    source: normalized.leaderContinuitySource,
    evidenceStatus: normalized.leaderContinuitySource === "typed" ? "canonical" : "fallback",
    approvalState: normalized.approvalState,
    ambiguityFlag: normalized.ambiguityFlag,
    ambiguityReasons: normalized.ambiguityReasons,
    leader: normalized.leader,
    leaderDevelopment: normalized.leaderDevelopment,
    coachDevelopment: normalized.coachDevelopment,
  };
}
